import { defineComponent } from 'vue';
import { QBtn, QTooltip } from 'quasar';
import AceEditor from '../3rdParty/AceEditor.jsx';

const btnProps = {
    color: 'primary',
    size: 'sm',
    padding: 'none sm',
};

/**
 * JSON editor for Control Designer dock tab. Defers Ace mount until the tab is active
 * and the host element is in the document (avoids empty editor on inactive dock panels).
 */
export default defineComponent({
    name: 'ControlDesignerJsonPanel',
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        content: {
            type: String,
            default: '',
        },
        mountKey: {
            type: Number,
            default: 0,
        },
        hasUncommittedEdits: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['change-content', 'cancel', 'sync-tree'],
    data() {
        return {
            showEditor: false,
            localDirty: false,
            _ignoreEditorChanges: false,
            cancelTooltipOpen: false,
            syncTreeTooltipOpen: false,
            followOutlineEnabled: true,
            followOutlineTooltipOpen: false,
        };
    },
    watch: {
        hasUncommittedEdits(val) {
            if (!val)
                this.localDirty = false;
        },
        mountKey() {
            this.localDirty = false;
            this.scheduleEditorMount(this.active, true);
        },
        active: {
            immediate: true,
            handler(active) {
                if (!active) {
                    this.localDirty = false;
                    this._ignoreEditorChanges = false;
                    this.cancelTooltipOpen = false;
                    this.syncTreeTooltipOpen = false;
                    this.followOutlineTooltipOpen = false;
                }
                this.scheduleEditorMount(active);
            },
        },
        content(value) {
            if (!this.hasUncommittedEdits)
                this.localDirty = false;
            if (value && this.active && (!this.showEditor || !this._aceRef?.editor))
                this.scheduleEditorMount(true);
        },
    },
    beforeUnmount() {
        this.cancelTooltipOpen = false;
        this.syncTreeTooltipOpen = false;
        this.followOutlineTooltipOpen = false;
        if (this._mountTimer != null) {
            clearTimeout(this._mountTimer);
            this._mountTimer = null;
        }
    },
    methods: {
        scheduleEditorMount(active, forceRemount = false) {
            if (this._mountTimer != null) {
                clearTimeout(this._mountTimer);
                this._mountTimer = null;
            }

            if (!active || !this.content) {
                this.showEditor = false;
                return;
            }

            if (!forceRemount && this.showEditor && this._aceRef?.editor)
                return;

            this.showEditor = false;
            this._mountTimer = setTimeout(() => {
                this._mountTimer = null;
                requestAnimationFrame(() => {
                    if (this.active && this.content)
                        this.showEditor = true;
                });
            }, 0);
        },
        onCancelClick() {
            this.cancelTooltipOpen = false;
            this.localDirty = false;
            this._ignoreEditorChanges = true;
            this.$emit('cancel');
        },
        onEditorChangeContent(text) {
            if (this._ignoreEditorChanges)
                return;
            if (text === this.content)
                return;
            this.localDirty = true;
            this.$emit('change-content', text);
        },
        onEditorChangeDelta() {
            if (this._ignoreEditorChanges)
                return;
            this.localDirty = true;
        },
        onSyncTreeClick() {
            this.syncTreeTooltipOpen = false;
            const offset = this._aceRef?.getCursorOffset?.();
            if (offset == null)
                return;
            this._ignoreEditorChanges = true;
            this.$emit('sync-tree', offset);
        },
        endSyncTreeInteraction() {
            this._ignoreEditorChanges = false;
            if (!this.hasUncommittedEdits)
                this.localDirty = false;
        },
        restoreCursorOffset(offset) {
            if (offset == null) {
                this.endSyncTreeInteraction();
                return;
            }

            const tryRestore = (attemptsLeft) => {
                if (this._aceRef?.editor) {
                    this._aceRef.setCursorOffset(offset);
                    this._aceRef.editor.focus();
                    this.endSyncTreeInteraction();
                    return;
                }
                if (attemptsLeft > 0)
                    setTimeout(() => tryRestore(attemptsLeft - 1), 0);
                else
                    this.endSyncTreeInteraction();
            };

            this.$nextTick(() => {
                tryRestore(8);
            });
        },
        isFollowOutlineEnabled() {
            return this.followOutlineEnabled;
        },
        navigateToOffset(offset) {
            if (offset == null)
                return;

            const tryNavigate = (attemptsLeft) => {
                if (this._aceRef?.editor) {
                    this._aceRef.setCursorOffset(offset, true);
                    this._aceRef.editor.focus();
                    return;
                }
                if (attemptsLeft > 0)
                    setTimeout(() => tryNavigate(attemptsLeft - 1), 0);
            };

            this.$nextTick(() => {
                tryNavigate(8);
            });
        },
    },
    render() {
        const panelStyle = {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            minHeight: 0,
            flex: 1,
            borderStyle: 'solid',
            borderWidth: '1px',
            boxSizing: 'border-box',
            overflow: 'hidden',
        };

        const cancelEnabled = this.hasUncommittedEdits || this.localDirty;

        return (
            <div class="control-designer-json-panel" style={panelStyle}>
                <div
                    class="control-designer-json-panel__toolbar"
                    style={{
                        display: 'flex',
                        flexShrink: 0,
                        alignItems: 'center',
                        padding: '4px 8px',
                        borderBottom: '1px solid silver',
                        backgroundColor: 'silver',
                        gap: '8px',
                    }}
                >
                    <span style="position: relative; display: inline-block;">
                        <QBtn
                            {...btnProps}
                            disable={!cancelEnabled}
                            onMouseenter={() => { this.cancelTooltipOpen = true; }}
                            onMouseleave={() => { this.cancelTooltipOpen = false; }}
                            onPointerdown={() => { this.cancelTooltipOpen = false; }}
                            onClick={() => this.onCancelClick()}
                        >
                            Cancel
                        </QBtn>
                        <QTooltip
                            modelValue={this.cancelTooltipOpen && this.active}
                            noParentEvent
                            anchor="bottom middle"
                            self="top middle"
                            transitionHide="scale"
                            transitionShow="scale"
                        >
                            <span style="font-size: larger;">
                                Discard JSON edits and return to the Designer tab
                            </span>
                        </QTooltip>
                    </span>
                    <span style="position: relative; display: inline-block;">
                        <QBtn
                            {...btnProps}
                            disable={!this.showEditor}
                            onMouseenter={() => { this.syncTreeTooltipOpen = true; }}
                            onMouseleave={() => { this.syncTreeTooltipOpen = false; }}
                            onPointerdown={() => {
                                this.syncTreeTooltipOpen = false;
                                this._ignoreEditorChanges = true;
                            }}
                            onClick={() => this.onSyncTreeClick()}
                        >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                Sync Outline
                                <i class="mdi mdi-file-tree" />
                            </span>
                        </QBtn>
                        <QTooltip
                            modelValue={this.syncTreeTooltipOpen && this.active}
                            noParentEvent
                            anchor="bottom middle"
                            self="top middle"
                            transitionHide="scale"
                            transitionShow="scale"
                        >
                            <span style="font-size: larger;">
                                Select the tree node at the JSON cursor (requires valid JSON; new nodes not yet in the model cannot be located)
                            </span>
                        </QTooltip>
                    </span>
                    <span style="position: relative; display: inline-block;">
                        <QBtn
                            {...btnProps}
                            disable={!this.showEditor}
                            icon={this.followOutlineEnabled ? 'mdi-sync' : 'mdi-sync-off'}
                            color={this.followOutlineEnabled ? 'primary' : 'grey'}
                            onMouseenter={() => { this.followOutlineTooltipOpen = true; }}
                            onMouseleave={() => { this.followOutlineTooltipOpen = false; }}
                            onPointerdown={() => { this.followOutlineTooltipOpen = false; }}
                            onClick={() => { this.followOutlineEnabled = !this.followOutlineEnabled; }}
                        />
                        <QTooltip
                            modelValue={this.followOutlineTooltipOpen && this.active}
                            noParentEvent
                            anchor="bottom middle"
                            self="top middle"
                            transitionHide="scale"
                            transitionShow="scale"
                        >
                            <span style="font-size: larger;">
                                {this.followOutlineEnabled
                                    ? 'Follow Outline is on: selecting a node in the Document Tree moves the JSON cursor to the matching element'
                                    : 'Follow Outline is off: click to enable automatic JSON cursor navigation from the Document Tree'}
                            </span>
                        </QTooltip>
                    </span>
                </div>
                {this.showEditor ? (
                    <AceEditor
                        ref={(el) => { this._aceRef = el; }}
                        key={`controlDesignerJsonEditor-${this.mountKey}`}
                        editorId="controlDesignerJsonEditor"
                        lang="json"
                        content={this.content}
                        onChangeContent={(text) => this.onEditorChangeContent(text)}
                        onChangeDelta={() => this.onEditorChangeDelta()}
                    />
                ) : null}
            </div>
        );
    },
});
